import React, { useState, useRef } from 'react';
import { motion, useInView } from 'motion/react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, MapPin, Send, CheckCircle, Instagram, Linkedin, ArrowUpRight } from 'lucide-react';
import { toast } from 'sonner';
import { contactContent } from '../data/mock';
import Layout from '../components/layout/Layout';
import SEOHead from '../components/SEOHead';

// Text Reveal Animation
const TextReveal = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: "100%", opacity: 0 }}
        animate={isInView ? { y: 0, opacity: 1 } : { y: "100%", opacity: 0 }}
        transition={{ duration: 0.7, delay, ease: [0.22, 1, 0.36, 1] }}
      >
        {children}
      </motion.div>
    </div>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/contact/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to send message');
      }
      
      const result = await response.json();
      toast.success(result.message || 'Message received! We\'ll get back to you within 24 hours.');
      setFormData({ name: '', email: '', service: '', message: '' });
    } catch (error) {
      console.error('Contact form error:', error);
      toast.error(error.message || 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Layout>
      <SEOHead
        title="Contact Buzz Craft Maven | Start a Project"
        description="Get in touch with Buzz Craft Maven for digital marketing, brand strategy, or interior design projects. Offices in Chennai, Hyderabad, and Dubai."
        canonical="https://buzzcraftmaven.com/contact"
        ogTitle="Contact Buzz Craft Maven"
        ogDescription="Start a project with our creative consultancy. Digital marketing and interior design solutions."
        schemas={[
          { "@context": "https://schema.org", "@type": "ContactPage", "name": "Contact Buzz Craft Maven", "url": "https://buzzcraftmaven.com/contact", "mainEntity": { "@type": "Organization", "name": "Buzz Craft Maven", "email": "buzzcraftmaven@gmail.com" } },
          { "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://buzzcraftmaven.com" }, { "@type": "ListItem", "position": 2, "name": "Contact", "item": "https://buzzcraftmaven.com/contact" }] }
        ]}
      />

      {/* HERO SECTION */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 bg-sunrise-hero relative overflow-hidden">
        <div className="absolute inset-0">
          <motion.div 
            className="absolute top-20 right-[20%] w-[400px] h-[400px] sun-orb sun-orb-primary"
            animate={{ scale: [1, 1.1, 1], opacity: [0.3, 0.5, 0.3] }}
            transition={{ duration: 8, repeat: Infinity }}
          />
        </div>

        <div className="container mx-auto px-6 md:px-12 relative z-10">
          <nav className="text-sm text-[#8A8A8A] mb-6" aria-label="Breadcrumb">
            <Link to="/" className="hover:text-[#FF6B35]">Home</Link> <span className="mx-2">/</span> <span className="text-[#2D2D2D]">Contact</span>
          </nav>
          <div className="max-w-2xl">
            <TextReveal>
              <span className="inline-block px-4 py-1.5 bg-[#FF6B35]/10 text-[#FF6B35] text-sm font-medium rounded-full mb-6">Get in Touch</span>
            </TextReveal>
            
            <TextReveal delay={0.1}>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl text-[#2D2D2D] leading-tight mb-4">
                Let's create
              </h1>
            </TextReveal>
            <TextReveal delay={0.15}>
              <h1 className="font-display text-4xl md:text-5xl lg:text-6xl leading-tight mb-8">
                <span className="text-gradient-sunrise">something beautiful.</span>
              </h1>
            </TextReveal>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-lg text-[#5A5A5A] max-w-lg"
            >
              Have a project in mind? A question to ask? Or just want to say hello? 
              We'd love to hear from you.
            </motion.p>
          </div>
        </div>
      </section>

      {/* CONTACT CONTENT */}
      <section className="section-padding bg-white">
        <div className="container mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
            {/* Contact Form */}
            <div className="lg:col-span-7">
              <h2 className="font-display text-2xl text-[#2D2D2D] mb-8">Send us a message</h2>
              
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[#5A5A5A] text-sm mb-2">Your Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      data-testid="contact-name"
                      className="input-warm"
                      placeholder="John Doe"
                    />
                  </div>
                  <div>
                    <label className="block text-[#5A5A5A] text-sm mb-2">Email Address</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      data-testid="contact-email"
                      className="input-warm"
                      placeholder="john@example.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-[#5A5A5A] text-sm mb-2">What do you need?</label>
                  <select
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    data-testid="contact-service"
                    className="input-warm cursor-pointer"
                  >
                    <option value="">Select a service...</option>
                    <optgroup label="Digital Marketing">
                      <option value="seo">SEO & Content Marketing</option>
                      <option value="social">Social Media Strategy</option>
                      <option value="ads">Paid Advertising</option>
                      <option value="brand">Brand Strategy</option>
                    </optgroup>
                    <optgroup label="Interior Design">
                      <option value="commercial-interior">Commercial Interior</option>
                      <option value="residential-interior">Residential Interior</option>
                    </optgroup>
                    <optgroup label="Photography">
                      <option value="brand-photography">Brand & Product</option>
                      <option value="corporate-photography">Corporate</option>
                    </optgroup>
                    <optgroup label="Tech & Development">
                      <option value="website">Website Development</option>
                      <option value="app">App Development</option>
                    </optgroup>
                    <option value="other">Something Else</option>
                  </select>
                </div>

                <div>
                  <label className="block text-[#5A5A5A] text-sm mb-2">Tell us about your project</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={5}
                    data-testid="contact-message"
                    className="input-warm resize-none"
                    placeholder="Describe your project, goals, and any specific requirements..."
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="contact-submit"
                  className="btn-sunrise px-8 py-4 disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="flex items-center gap-2">
                    {isSubmitting ? (
                      <>
                        <motion.div 
                          className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                          animate={{ rotate: 360 }}
                          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                        />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <Send size={18} />
                      </>
                    )}
                  </span>
                </motion.button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="lg:col-span-5 space-y-6">
              {/* Quick Contact Card */}
              <div className="p-6 md:p-8 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/10">
                <h3 className="text-lg font-semibold text-[#2D2D2D] mb-6">Quick Contact</h3>
                <a
                  href="https://mail.google.com/mail/?view=cm&fs=1&to=buzzcraftmaven@gmail.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="contact-email-link"
                  className="flex items-center gap-4 p-4 bg-white rounded-xl border border-[#FF6B35]/10 hover:border-[#FF6B35]/30 hover:shadow-lg transition-all group"
                >
                  <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center">
                    <Mail className="text-white" size={20} />
                  </div>
                  <div>
                    <p className="text-[#8A8A8A] text-sm">Email us at</p>
                    <p className="text-[#2D2D2D] font-medium group-hover:text-[#FF6B35] transition-colors">{contactContent.email}</p>
                  </div>
                  <ArrowUpRight className="text-[#8A8A8A] group-hover:text-[#FF6B35] ml-auto transition-colors" size={18} />
                </a>
                
                {/* Social Links */}
                <div className="mt-6 flex items-center gap-3">
                  <a
                    href="https://www.instagram.com/buzzcraftmaven/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-instagram"
                    className="w-10 h-10 rounded-full bg-white border border-[#FF6B35]/20 flex items-center justify-center text-[#5A5A5A] hover:text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-all"
                  >
                    <Instagram size={18} />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/buzzcraftmaven/"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="contact-linkedin"
                    className="w-10 h-10 rounded-full bg-white border border-[#FF6B35]/20 flex items-center justify-center text-[#5A5A5A] hover:text-white hover:bg-[#FF6B35] hover:border-[#FF6B35] transition-all"
                  >
                    <Linkedin size={18} />
                  </a>
                </div>
              </div>

              {/* Offices Card */}
              <div className="p-6 md:p-8 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/10">
                <h3 className="text-lg font-semibold text-[#2D2D2D] mb-6">Our Offices</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-[#FF6B35] mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-[#2D2D2D] font-medium">Chennai (HQ)</p>
                      <p className="text-[#8A8A8A] text-sm">AVM Avenue, 8th Street</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-[#8A8A8A] mt-0.5 flex-shrink-0" />
                    <p className="text-[#5A5A5A]">Hyderabad</p>
                  </div>
                  <div className="flex items-start gap-3">
                    <MapPin size={18} className="text-[#8A8A8A] mt-0.5 flex-shrink-0" />
                    <p className="text-[#5A5A5A]">Dubai, UAE</p>
                  </div>
                </div>
              </div>

              {/* We Work With Card */}
              <div className="p-6 md:p-8 bg-[#FFFBF5] rounded-2xl border border-[#FF6B35]/10">
                <h3 className="text-lg font-semibold text-[#2D2D2D] mb-4">We love working with</h3>
                <div className="flex flex-wrap gap-2">
                  {['Startups', 'Restaurants', 'Real Estate', 'E-commerce', 'Personal Brands', 'Agencies'].map((tag) => (
                    <span
                      key={tag}
                      className="px-3 py-1.5 bg-white text-[#5A5A5A] text-sm rounded-full border border-[#FF6B35]/10"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>

              {/* What Happens Next Card */}
              <div className="p-6 md:p-8 rounded-2xl bg-gradient-to-br from-[#FF6B35]/10 to-[#FFB627]/10 border border-[#FF6B35]/20">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#FF6B35] to-[#FFB627] flex items-center justify-center flex-shrink-0">
                    <CheckCircle className="text-white" size={20} />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-[#2D2D2D] mb-3">What happens next?</h3>
                    <ol className="text-[#5A5A5A] text-sm space-y-2">
                      <li className="flex items-start gap-2">
                        <span className="text-[#FF6B35] font-semibold">1.</span>
                        We'll review your message within 24 hours
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#FF6B35] font-semibold">2.</span>
                        Schedule a discovery call to understand your needs
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="text-[#FF6B35] font-semibold">3.</span>
                        Create a tailored proposal for your project
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Contact;
